import { cn } from "@faire/prototype-playground/lib/utils";
import type { ReactNode } from "react";

import { usePlaygroundImageWithFallback } from "../../hooks/usePlaygroundImageWithFallback";

export interface MarketplaceProductTileData {
  token: string;
  name: string;
  imageUrl: string;
  /** Wholesale price, formatted (e.g. "$14.99"). */
  price: string;
  /** MSRP line, formatted (e.g. "MSRP $29.99"). */
  msrp?: string;
  /** Case size line (e.g. "Case of 6"). */
  caseSize?: string;
  /** Top-left overlay badge. */
  badge?: "Bestseller" | "New" | "Trending";
  /** Secondary line under the name (e.g. "2 sizes"). */
  sizes?: string;
  /** Color/variant swatch image URLs, shown between image and price. */
  swatches?: string[];
  /** When true, shows a "Preorder" critical label beside the case size. */
  preorder?: boolean;
  /** Expected ship range, shown under the case size for preorders. */
  shipRange?: string;
  /** Scarcity line (e.g. "Only 4 cases left"), shown beside the case size. */
  scarcity?: string;
}

export interface MarketplaceProductTileProps {
  product: MarketplaceProductTileData;
  isFavorited?: boolean;
  onFavorite?: () => void;
  onQuickAdd?: () => void;
  className?: string;
  /**
   * Hides the overlay quick-add plus, mirroring the desktop search treatment
   * where a footer Add to Cart button replaces the overlay control.
   */
  hideQuickAdd?: boolean;
  /** Controls layered over the product image, e.g. discovery pills. */
  imageOverlay?: ReactNode;
  /** Replaces the swatch row between the image and the price block. */
  belowImage?: ReactNode;
  /** Content appended after the case-size block, e.g. a footer Add to Cart. */
  footer?: ReactNode;
}

/**
 * The marketplace catalog product tile used in brand-storefront and search grids.
 * Square image with a favorite heart, round quick-add, optional overlay badge,
 * price + MSRP, name, "Case of X", swatches, and preorder/scarcity states.
 */
export default function MarketplaceProductTile({
  product,
  isFavorited = false,
  onFavorite,
  onQuickAdd,
  className,
  hideQuickAdd = false,
  imageOverlay,
  belowImage,
  footer,
}: MarketplaceProductTileProps) {
  const { src, onError } = usePlaygroundImageWithFallback(product.imageUrl);

  return (
    <div className={cn("relative flex flex-col", className)}>
      <div className="relative">
        {/* Image */}
        <div className="rounded-fs-component-default relative aspect-square w-full overflow-hidden">
          <img
            src={src}
            alt={product.name}
            onError={onError}
            referrerPolicy="no-referrer"
            className="absolute top-0 left-0 h-full w-full object-cover object-center"
          />
          <div className="bg-fs-action-surface-active/2 pointer-events-none absolute inset-0 z-2" />
        </div>

        {/* Overlay badge (top-left) */}
        {product.badge ? (
          <div className="absolute top-2 left-2 z-3">
            <TileBadge label={product.badge} />
          </div>
        ) : null}

        {/* Favorite (top-right) */}
        <button
          type="button"
          aria-label={isFavorited ? "Saved to board" : "Save product to board"}
          onClick={onFavorite}
          className="absolute top-1 right-1 z-3 p-2"
        >
          <HeartIcon filled={isFavorited} />
        </button>

        {/* Quick add (bottom-right) */}
        {hideQuickAdd ? null : (
          <button
            type="button"
            aria-label="Quick add"
            onClick={onQuickAdd}
            className="border-fs-border-muted bg-fs-surface-primary text-fs-text-active hover:border-fs-action-border-active hover:bg-fs-surface-secondary absolute right-2 bottom-2 z-5 flex size-8 items-center justify-center rounded-2xl border transition-all duration-200"
          >
            <PlusIcon />
          </button>
        )}

        {imageOverlay}
      </div>

      {/* Swatches */}
      {belowImage ??
        (product.swatches && product.swatches.length > 0 ? (
          <div className="flex flex-row items-center gap-0.5 pt-1 pb-2">
            {product.swatches.map((swatchUrl, index) => (
              <span
                key={swatchUrl}
                className="flex size-6 items-center justify-center lg:size-5"
              >
                <img
                  src={swatchUrl}
                  alt={`Swatch ${index + 1}`}
                  className="border-fs-border-muted size-5 rounded-full border object-cover lg:size-4"
                />
              </span>
            ))}
          </div>
        ) : (
          <div className="h-2" />
        ))}

      {/* Price + name */}
      <div className="flex flex-col gap-0.5">
        <div className="flex flex-wrap items-baseline gap-x-1">
          <span className="type-fs-subheading-medium text-fs-text-primary">
            {product.price}
          </span>
          {product.msrp ? (
            <span className="type-fs-label text-fs-text-primary">
              {product.msrp}
            </span>
          ) : null}
        </div>
        <p className="type-fs-paragraph-medium text-fs-text-primary line-clamp-2 break-words">
          {product.name}
        </p>
        {product.sizes ? (
          <p className="type-fs-paragraph text-fs-text-primary">
            {product.sizes}
          </p>
        ) : null}
      </div>

      {/* Case size + states */}
      <div className="mt-1 flex flex-col gap-0.5">
        <div className="flex w-full flex-wrap items-baseline gap-2">
          {product.caseSize ? (
            <p className="type-fs-paragraph text-fs-text-subdued">
              {product.caseSize}
            </p>
          ) : null}
          {product.preorder ? (
            <p className="type-fs-paragraph text-fs-message-text-critical">
              Preorder
            </p>
          ) : null}
          {product.scarcity ? (
            <p className="type-fs-paragraph text-fs-message-text-critical">
              {product.scarcity}
            </p>
          ) : null}
        </div>
        {product.shipRange ? (
          <p className="type-fs-paragraph text-fs-text-primary">
            {product.shipRange}
          </p>
        ) : null}
      </div>

      {footer}
    </div>
  );
}

function TileBadge({ label }: { label: string }) {
  return (
    <div className="border-fs-surface-primary text-fs-text-primary-inverse bg-fs-surface-primary-inverse type-fs-label-medium inline rounded border border-solid px-[3px] py-[3px] lg:px-[7px]">
      {label}
    </div>
  );
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill={filled ? "#333333" : "none"}
      stroke="#333333"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 21.343 2.539 12.69C-2.571 7.58 4.949-2.33 12 5.662c7.05-7.99 14.572 1.918 9.461 7.029z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 1.714v20.572M1.714 12h20.572" />
    </svg>
  );
}
